import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { CartItem } from '../context/CartContext';
import { RepairService } from '../types/BookingTypes';
import ServiceDetailsModal from '../components/ServiceDetailsModal';
import { createPaymentOrder, initializePayment } from '../services/paymentService';
import { isLoggedIn, getCurrentUser } from '../services/authService';
import { createOrder } from '../services/orderService';
import Header from '../components/Header';
import { trackEvent, ClarityEvents } from '../utils/clarityTracking';

interface Address {
  id: string;
  label: string;
  address: string;
  landmark?: string;
  latitude: number;
  longitude: number;
  isDefault: boolean;
}

interface PaymentMethod {
  id: string;
  name: string;
  icon: string;
  type: 'UPI' | 'CARD' | 'NET_BANKING' | 'WALLET' | 'COD';
}

const paymentMethods: PaymentMethod[] = [
  { id: 'online', name: 'Online Payment', icon: 'fas fa-mobile-alt', type: 'UPI' },
  { id: 'cod', name: 'Cash on Delivery', icon: 'fas fa-money-bill-wave', type: 'COD' },
];

const CartPage: React.FC = () => {
  const { cart, removeFromCart, clearCart } = useCart();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<RepairService | null>(null);
  const [defaultAddress, setDefaultAddress] = useState<Address | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<string>('online');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    // Track cart view
    trackEvent(ClarityEvents.CART_VIEW, {
      itemCount: cart.length,
      totalAmount: calculateBill().total
    });
    
    // Load addresses from localStorage and find default
    const savedAddresses = localStorage.getItem('minto_addresses');
    if (savedAddresses) {
      const addresses: Address[] = JSON.parse(savedAddresses);
      const defaultAddr = addresses.find(addr => addr.isDefault);
      setDefaultAddress(defaultAddr || null);
    }
  }, []);

  const calculateBill = () => {
    let totalWithGST = 0;

    cart.forEach((item: CartItem) => {
      item.services.forEach((service: RepairService) => {
        const totalPrice = service.finalPrice || (service.actualPrice + (service.serviceCost || 0) + ((service.serviceCost || 0) * 0.18));
        totalWithGST += totalPrice;
      });
    });

    const doorstepCharges = 250;
    const total = totalWithGST;

    return {
      itemsTotal: totalWithGST,
      serviceTotal: 0,
      gstOnService: 0,
      doorstepCharges,
      onlinePaymentDiscount: 0,
      total
    };
  };

  const handleServiceClick = (service: RepairService) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const bill = calculateBill();

  const handlePayment = async () => {
    if (!defaultAddress) {
      alert('Please add a delivery address');
      return;
    }

    // Check if user is logged in
    if (!isLoggedIn()) {
      // Track login requirement
      trackEvent(ClarityEvents.LOGIN_ATTEMPT, {
        source: 'cart_checkout',
        returnUrl: '/cart'
      });
      
      // Redirect to login page with return URL
      navigate(`/login?returnUrl=${encodeURIComponent('/cart')}`);
      return;
    }

    try {
      setIsProcessing(true);

      // Get current user
      const user = getCurrentUser();
      if (!user) {
        throw new Error('User not found');
      }

      // Track order initiation
      trackEvent(ClarityEvents.ORDER_INITIATE, {
        itemCount: cart.length,
        totalAmount: bill.total,
        paymentMethod: selectedPayment
      });

      // Create order first
      const order = await createOrder(
        cart,
        user,
        defaultAddress,
        selectedPayment,
        bill
      );

      // Track order placement
      trackEvent(ClarityEvents.ORDER_PLACE, {
        orderId: order.id,
        amount: bill.total,
        paymentMethod: selectedPayment
      });

      // Clear the cart after successful order creation
      clearCart();

      // For both COD and online payment, navigate to order summary
      navigate(`/order-summary/${order.id}`, { state: { order } });
    } catch (err) {
      console.error('Payment error:', err);
      // Track payment failure
      trackEvent(ClarityEvents.PAYMENT_FAILURE, {
        error: err instanceof Error ? err.message : 'Unknown error occurred'
      });
      alert('Failed to process payment. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="Cart" showBack={true} />

      <div className="container mx-auto px-4 py-4 pb-24">
        {cart.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-4 text-gray-300">
              <i className="fas fa-shopping-cart text-6xl"></i>
            </div>
            <h2 className="text-xl font-medium text-gray-600 mb-2">Your cart is empty</h2>
            <p className="text-gray-500 mb-6">Add some repair services to get started</p>
            <div className="flex justify-center">
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2.5 rounded-xl font-medium text-base text-white bg-minto-500 hover:bg-minto-600 active:bg-minto-700 transition-colors shadow-md hover:shadow-lg flex items-center justify-center"
              >
                <i className="fas fa-tools mr-2"></i>
                Browse Services
              </button>
            </div>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-3">
            {/* Cart Items */}
            <div className="md:col-span-2 space-y-4">
              {cart.map((item: CartItem) => (
                <div key={item.model.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                  {/* Device Info */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center">
                      <i className="fas fa-mobile-alt text-minto-500 mr-2"></i>
                      <h3 className="font-medium text-gray-900">{item.model.name}</h3>
                    </div>
                  </div>

                  {/* Services List */}
                  <div className="divide-y divide-gray-100">
                    {item.services.map((service: RepairService) => (
                      <div key={service.id} className="flex flex-col">
                        <div className="p-4 flex items-start">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-50 rounded-lg p-2 mr-3 flex items-center justify-center">
                            <img
                              src={service.serviceImage}
                              alt={service.serviceName}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <h4 className="font-medium text-gray-900 text-sm mb-0.5">{service.serviceName}</h4>
                                <div className="text-xs text-gray-500">{service.serviceDuration} mins to repair</div>
                              </div>
                              <div className="flex-shrink-0 text-right">
                                <div className="text-sm font-semibold text-blue-600">₹{Math.round(service.finalPrice || (service.actualPrice + (service.serviceCost || 0) + ((service.serviceCost || 0) * 0.18)))}</div>
                                <div className="text-xs text-gray-500">Includes service charge & taxes</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Warranty Info */}
                        <div className="px-4 py-2 bg-green-50 border-t border-green-100">
                          <div className="flex items-center gap-2 text-green-700">
                            <i className="fas fa-shield-alt text-sm"></i>
                            <span className="text-sm font-medium">
                              {service.warrantyPeriod || 90} Days Warranty till {new Date(Date.now() + ((service.warrantyPeriod || 90) * 24 * 60 * 60 * 1000)).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                        </div>

                        {/* Service Actions */}
                        <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-between">
                          <button
                            onClick={() => handleServiceClick(service)}
                            className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                          >
                            <i className="fas fa-info-circle"></i>
                            View Details
                          </button>
                          <button
                            onClick={() => removeFromCart(item.model.id, service.id)}
                            className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                          >
                            <i className="fas fa-trash-alt"></i>
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Right Column: Bill Details, Address and Payment */}
            <div className="space-y-4">
              {/* Bill Details */}
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Bill Details</h2>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center text-gray-600">
                    <span>Items Total (including service charges & taxes)</span>
                    <span>₹{Math.round(bill.itemsTotal)}</span>
                  </div>
                  <div className="flex justify-between items-center text-gray-600">
                    <div className="flex items-center">
                      <span>Doorstep Charges</span>
                      <span className="ml-1 text-green-600 text-xs">(Waived)</span>
                    </div>
                    <span className="line-through text-gray-400">₹{bill.doorstepCharges.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-gray-100 pt-3 mt-3">
                    <div className="flex justify-between items-center font-medium text-base text-gray-900">
                      <span>Total Amount</span>
                      <span>₹{Math.round(bill.total)}</span>
                    </div>
                    <p className="text-xs text-green-600 mt-1">
                      You saved ₹{bill.doorstepCharges.toFixed(2)} on doorstep charges
                    </p>
                  </div>
                </div>
              </div>

              {/* Address Section */}
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Delivery Address</h2>
                {defaultAddress ? (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-gray-900">{defaultAddress.label}</span>
                      <span className="px-2 py-0.5 text-xs font-medium text-green-700 bg-green-50 rounded-full">
                        Default
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{defaultAddress.address}</p>
                    {defaultAddress.landmark && (
                      <p className="text-sm text-gray-500 mt-1">Landmark: {defaultAddress.landmark}</p>
                    )}
                    <button
                      onClick={() => navigate('/address')}
                      className="mt-3 text-sm text-minto-600 hover:text-minto-700 flex items-center gap-1"
                    >
                      <i className="fas fa-edit"></i>
                      Change Address
                    </button>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-600 mb-3">No delivery address set</p>
                    <button
                      onClick={() => navigate('/address')}
                      className="w-full px-4 py-2.5 bg-minto-500 text-white rounded-lg hover:bg-minto-600 active:bg-minto-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <i className="fas fa-plus"></i>
                      Add Delivery Address
                    </button>
                  </div>
                )}
              </div>

              {/* Payment Methods */}
              <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
                <div className="flex items-center gap-1.5 mb-3">
                  <i className="fas fa-credit-card text-minto-500"></i>
                  <h2 className="text-base font-semibold text-gray-900">Payment Method</h2>
                </div>
                <div className="space-y-2">
                  {paymentMethods.map((method) => (
                    <label
                      key={method.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedPayment === method.id
                          ? 'border-minto-500 bg-minto-50'
                          : 'border-gray-200 hover:border-minto-200'
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment"
                        value={method.id}
                        checked={selectedPayment === method.id}
                        onChange={(e) => setSelectedPayment(e.target.value)}
                        className="hidden"
                      />
                      <i className={`${method.icon} text-lg text-gray-600`}></i>
                      <span className="flex-1 text-sm font-medium text-gray-900 flex items-center gap-2">
                        {method.name}
                      </span>
                      <i className="fas fa-check text-minto-500 opacity-0 transition-opacity ${
                        selectedPayment === method.id ? 'opacity-100' : ''
                      }"></i>
                    </label>
                  ))}
                </div>
              </div>

              {/* Support Card */}
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-1.5 mb-3">
                  <i className="fas fa-headset text-minto-500"></i>
                  <h2 className="text-base font-semibold text-gray-900">Need Help?</h2>
                </div>
                <div className="space-y-3">
                  <a 
                    href="tel:+919324299258" 
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                  >
                    <div className="w-10 h-10 rounded-full bg-minto-100 flex items-center justify-center flex-shrink-0 group-hover:bg-minto-200 transition-colors">
                      <i className="fas fa-phone-alt text-minto-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Call Support</h3>
                      <span className="text-sm text-minto-600">
                        +91 93242 99258
                      </span>
                    </div>
                  </a>
                  <a 
                    href="https://wa.me/+919324299258?text=Hi,%20I%20need%20help%20with%20my%20cart" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                  >
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 group-hover:bg-green-200 transition-colors">
                      <i className="fab fa-whatsapp text-green-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">WhatsApp Support</h3>
                      <span className="text-sm text-green-600">
                        Chat with us
                      </span>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile-style footer */}
      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-[0_-8px_16px_-4px_rgba(0,0,0,0.1)] border-t border-gray-200">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="text-right">
                  <div className="text-xs text-gray-500">Total Amount</div>
                  <div className="text-lg font-bold text-minto-500">₹{Math.round(bill.total)}</div>
                </div>
              </div>
              <button
                onClick={handlePayment}
                disabled={!defaultAddress || isProcessing}
                className={`px-6 py-2.5 rounded-xl font-medium text-base text-white transition-colors shadow-md hover:shadow-lg ${
                  defaultAddress && !isProcessing
                    ? 'bg-minto-500 hover:bg-minto-600 active:bg-minto-700' 
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                {isProcessing ? (
                  <div className="flex items-center gap-2">
                    <i className="fas fa-spinner fa-spin"></i>
                    Processing...
                  </div>
                ) : defaultAddress ? (
                  'Place Order'
                ) : (
                  'Add Address to Continue'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add payment container div */}
      <div id="payment-dropin" className="hidden"></div>

      {/* Service Details Modal */}
      <ServiceDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        service={selectedService}
      />
    </div>
  );
};

export default CartPage; 